import React, { useState } from 'react';
import { LinkType } from '../types';
import { v4 as uuidv4 } from 'uuid';

interface LinkItemProps {
  link: LinkType;
  links: LinkType[];
  onLinksChange: (links: LinkType[]) => void;
  onPreview: (link: LinkType) => void;
  isMobile?: boolean;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onDelete: (id: string) => void;
}

const STATUS_OPTIONS = [
  { value: 'pending', label: 'ממתין', bg: 'bg-[#fef08a] text-yellow-800' },
  { value: 'in-progress', label: 'בטיפול', bg: 'bg-[#bfdbfe] text-blue-800' },
  { value: 'approved', label: 'אושר', bg: 'bg-[#bbf7d0] text-green-800' },
  { value: 'archived', label: 'בארכיון', bg: 'bg-[#e2e8f0] text-gray-800' },
];

const LinkItem: React.FC<LinkItemProps> = ({
  link,
  links,
  onLinksChange,
  onPreview,
  isMobile,
  selectedId,
  onSelect,
  onDelete,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showTagInput, setShowTagInput] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [titleInput, setTitleInput] = useState(link.title || '');
  const [urlInput, setUrlInput] = useState(link.url || '');
  const [descInput, setDescInput] = useState(link.description || '');
  const isSelected = selectedId === link.id;

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData(
      'application/json',
      JSON.stringify({ type: 'link', id: link.id })
    );
  };

  const handleSave = () => {
    let favicon = '';
    try {
      const u = new URL(urlInput);
      favicon = `${u.protocol}//${u.hostname}/favicon.ico`;
    } catch {
      // ignore
    }

    onLinksChange(
      links.map((l) =>
        l.id === link.id
          ? { ...l, title: titleInput.trim(), url: urlInput.trim(), description: descInput.trim(), favicon }
          : l
      )
    );
    setIsEditing(false);
  };

  const handleAddTag = (e: React.FormEvent) => {
    e.preventDefault();
    if (tagInput.trim() && !link.tags.includes(tagInput.trim())) {
      onLinksChange(
        links.map((l) =>
          l.id === link.id ? { ...l, tags: [...l.tags, tagInput.trim()] } : l
        )
      );
    }
    setTagInput('');
    setShowTagInput(false);
  };

  const handleRemoveTag = (tagToRemove: string) => {
    onLinksChange(
      links.map((l) =>
        l.id === link.id ? { ...l, tags: l.tags.filter((t) => t !== tagToRemove) } : l
      )
    );
  };

  const handleStatusChange = (status: 'in-progress' | 'approved' | 'archived' | 'pending') => {
    onLinksChange(
      links.map((l) => (l.id === link.id ? { ...l, status } : l))
    );
  };

  const activeStatus = STATUS_OPTIONS.find((s) => s.value === link.status) || STATUS_OPTIONS[0];

  const content = (
    <div className="font-heebo flex flex-col gap-2">
      {isEditing ? (
        <div className="flex flex-col gap-1.5" onClick={(e) => e.stopPropagation()}>
          <input
            type="text"
            className="w-full px-2 py-1 border border-solid border-black text-xs outline-none bg-white rounded-none shadow-none"
            placeholder="שם הקישור"
            value={titleInput}
            onChange={(e) => setTitleInput(e.target.value)}
            dir="rtl"
          />
          <input
            type="text"
            className="w-full px-2 py-1 border border-solid border-black text-xs outline-none bg-white rounded-none shadow-none"
            placeholder="כתובת קישור (URL)"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            dir="ltr"
          />
          <input
            type="text"
            className="w-full px-2 py-1 border border-solid border-black text-xs outline-none bg-white rounded-none shadow-none"
            placeholder="תיאור קצר על הקישור..."
            value={descInput}
            onChange={(e) => setDescInput(e.target.value)}
            dir="rtl"
          />
          <div className="flex gap-1">
            <button
              onClick={handleSave}
              className="px-2 py-1 bg-black text-white text-xs font-bold border border-solid border-black hover:bg-gray-800 rounded-none cursor-pointer"
            >
              שמור
            </button>
            <button
              onClick={() => setIsEditing(false)}
              className="px-2 py-1 bg-white text-black text-xs font-bold border border-solid border-black hover:bg-gray-100 rounded-none cursor-pointer"
            >
              ביטול
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-1.5 justify-between">
            <div className="flex items-center gap-1.5 truncate flex-1 min-w-0">
              {link.favicon && (
                <img
                  src={link.favicon}
                  alt=""
                  className="w-4 h-4 object-contain flex-shrink-0"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              )}
              <span
                className="font-bold text-xs truncate text-black hover:underline cursor-pointer"
                onClick={() => onPreview(link)}
                title={link.url}
              >
                {link.title || link.url || 'ללא שם'}
              </span>
            </div>

            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditing(true);
                  setTitleInput(link.title || '');
                  setUrlInput(link.url || '');
                  setDescInput(link.description || '');
                }}
                className="p-1 border border-solid border-black bg-white hover:bg-gray-100 text-[10px] rounded-none cursor-pointer"
                title="ערוך קישור"
              >
                ✏️
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const newLink: LinkType = {
                    ...link,
                    id: uuidv4(),
                    title: `${link.title} (עותק)`,
                    createdAt: Date.now(),
                  };
                  onLinksChange([...links, newLink]);
                }}
                className="p-1 border border-solid border-gray-400 bg-white hover:bg-gray-100 text-gray-600 text-[10px] rounded-none cursor-pointer font-bold"
                title="צור עותק"
              >
                ⧉
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm('האם למחוק קישור זה לצמיתות?')) {
                    onDelete(link.id);
                  }
                }}
                className="p-1 border border-solid border-red-500 bg-red-50 hover:bg-red-100 text-red-600 text-[10px] rounded-none cursor-pointer font-bold"
                title="מחק קישור"
              >
                ✕
              </button>
            </div>
          </div>

          {link.url ? (
            <a
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] text-blue-700 underline truncate block hover:text-blue-900 select-all"
              dir="ltr"
              onClick={(e) => e.stopPropagation()}
            >
              {link.url}
            </a>
          ) : (
            <div className="text-[10px] text-gray-400 truncate">אין כתובת URL</div>
          )}
          {link.description && (
            <div className="text-[11px] text-gray-500 leading-tight" dir="rtl">
              {link.description}
            </div>
          )}
        </>
      )}

      {/* Flat Status and Tags section */}
      <div className="flex flex-wrap items-center gap-1.5 pt-1 border-t border-solid border-gray-200">
        {/* Status selection bubble */}
        <select
          value={link.status}
          onChange={(e) => handleStatusChange(e.target.value as any)}
          className={`text-[10px] font-bold px-1.5 py-0.5 border border-solid border-black rounded-none outline-none cursor-pointer ${activeStatus.bg}`}
          onClick={(e) => e.stopPropagation()}
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        {/* Flat Text Tags */}
        {link.tags.map((tag) => (
          <span
            key={tag}
            className="text-[9px] bg-white border border-solid border-gray-400 text-gray-800 px-1 py-0.5 flex items-center gap-0.5 rounded-none font-bold"
          >
            #{tag}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveTag(tag);
              }}
              className="hover:text-red-500 font-bold"
            >
              ✕
            </button>
          </span>
        ))}

        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowTagInput(!showTagInput);
          }}
          className="text-[9px] border border-solid border-black hover:bg-gray-100 px-1 py-0.5 rounded-none font-bold bg-white text-black cursor-pointer"
        >
          +תגית
        </button>
      </div>

      {showTagInput && (
        <form
          onSubmit={handleAddTag}
          className="flex gap-1 mt-1"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="text"
            className="flex-1 bg-white border border-solid border-black p-1 text-[10px] outline-none rounded-none shadow-none"
            placeholder="תגית חדשה"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            dir="rtl"
            autoFocus
          />
          <button
            type="submit"
            className="text-[10px] bg-black text-white px-2 border border-solid border-black hover:bg-gray-800 rounded-none cursor-pointer"
          >
            הוסף
          </button>
        </form>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <div
        onClick={() => onSelect(link.id)}
        className={`p-3 border border-solid bg-white transition-all rounded-none shadow-none ${
          isSelected ? 'border-pink-500 border-2 ring-2 ring-pink-500/20' : 'border-black'
        }`}
      >
        {content}
      </div>
    );
  }

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(link.id);
      }}
      onDoubleClick={() => onPreview(link)}
      className={`absolute p-3 border border-solid cursor-grab active:cursor-grabbing bg-white transition-all rounded-none shadow-none ${
        isSelected ? 'border-pink-500 border-2 ring-2 ring-pink-500/20' : 'border-black'
      }`}
      style={{
        left: link.position.x,
        top: link.position.y,
        minWidth: 220,
        maxWidth: 280,
        zIndex: isSelected ? 30 : 10,
      }}
    >
      {content}
    </div>
  );
};

export default LinkItem;